import { SerializedError } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../redux/hooks';
import { Trip } from '../redux/trip/trip.types';
import { selectIsAuthInitialized } from '../redux/user/user.selectors';
import { AuthUser } from '../redux/user/user.types';

/**
 * useAccessControl - Custom React Hook for Dynamic Trip Access Control
 *
 * Prevents false 403 redirects by waiting for Firebase authentication to fully
 * initialize before performing access checks. This eliminates the issue where
 * authenticated users were incorrectly redirected to 403 page during auth loading.
 *
 * BEHAVIOR:
 * - Waits for auth initialization to complete before checking access
 * - Only redirects if auth is fully initialized and user truly lacks access
 * - Prevents race conditions between auth loading and access checks
 * - Handles role-based access and public trip access appropriately
 *
 * @param currentTrip - The currently loaded trip object (undefined if loading)
 * @param currentUser - Firebase authenticated user (null if not authenticated, undefined if loading)
 * @param tripError - Any existing trip error (prevents duplicate redirects)
 */
const useAccessControl = (
  currentTrip: Trip | undefined,
  currentUser: AuthUser | null | undefined,
  tripError: Error | SerializedError | undefined
) => {
  const navigate = useNavigate();
  const isAuthInitialized = useAppSelector(selectIsAuthInitialized);

  useEffect(() => {
    // CRITICAL: Wait for auth initialization to prevent false 403 redirects
    if (!isAuthInitialized) {
      return;
    }

    // Only check access if we have a loaded trip and no existing errors
    if (currentTrip && !tripError) {
      // Check if user has explicit role-based access (owner, editor, viewer)
      const hasRoleAccess =
        currentUser && currentTrip.roles && currentTrip.roles[currentUser.uid] !== undefined;

      // Check if trip is publicly accessible (no authentication required)
      const isPublicTrip = currentTrip.public;

      // If user has neither role access nor public access, redirect to forbidden page
      if (!hasRoleAccess && !isPublicTrip) {
        navigate('/forbidden');
      }
    }
  }, [currentTrip, currentUser, navigate, tripError, isAuthInitialized]);
};

export default useAccessControl;
